import { supabase } from './supabase';
import { realtimeService } from './realtimeService';
import { Conversation, Message, MessageRequest, User } from '../types';

// Conversation management
export const getConversations = async (userId: string): Promise<Conversation[]> => {
  try {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        participants:conversation_participants (
          user_id,
          profiles:user_id (
            id,
            username,
            full_name,
            avatar_url
          )
        ),
        last_message:messages (
          id,
          content,
          message_type,
          created_at,
          sender:profiles!messages_sender_id_fkey (
            id,
            username,
            full_name,
            avatar_url
          )
        )
      `)
      .contains('participant_ids', [userId])
      .order('last_message_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(conv => ({
      id: conv.id,
      participant_ids: conv.participant_ids,
      participants: conv.participants?.map((p: any) => ({
        id: p.profiles?.id || p.user_id || '',
        username: p.profiles?.username || 'unknown',
        displayName: p.profiles?.full_name || p.profiles?.username || 'Unknown User',
        profileImage: p.profiles?.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
        bio: '',
        followerCount: 0,
        followingCount: 0,
        postCount: 0,
      })) || [],
      last_message: conv.last_message ? {
        id: conv.last_message.id,
        conversation_id: conv.id,
        sender_id: conv.last_message.sender?.id || conv.last_message.sender_id || '',
        sender: {
          id: conv.last_message.sender?.id || conv.last_message.sender_id || '',
          username: conv.last_message.sender?.username || 'unknown',
          displayName: conv.last_message.sender?.full_name || conv.last_message.sender?.username || 'Unknown User',
          profileImage: conv.last_message.sender?.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
          bio: '',
          followerCount: 0,
          followingCount: 0,
          postCount: 0,
        },
        content: conv.last_message.content,
        message_type: conv.last_message.message_type,
        created_at: conv.last_message.created_at,
        updated_at: conv.last_message.created_at,
        is_read: true,
        is_deleted: false,
      } : undefined,
      last_message_at: conv.last_message_at,
      created_at: conv.created_at,
      updated_at: conv.updated_at,
      unread_count: 0, // This would need a separate query
      is_group: conv.is_group,
      group_name: conv.group_name,
      group_image: conv.group_image,
    }));
  } catch (error) {
    console.error('Error loading conversations:', error);
    return [];
  }
};

export const getConversation = async (conversationId: string, userId: string): Promise<Conversation | null> => {
  try {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        participants:conversation_participants (
          user_id,
          profiles:user_id (
            id,
            username,
            full_name,
            avatar_url
          )
        )
      `)
      .eq('id', conversationId)
      .contains('participant_ids', [userId])
      .single();

    if (error) throw error;

    return {
      id: data.id,
      participant_ids: data.participant_ids,
      participants: data.participants?.map((p: any) => ({
        id: p.profiles?.id || p.user_id || '',
        username: p.profiles?.username || 'unknown',
        displayName: p.profiles?.full_name || p.profiles?.username || 'Unknown User',
        profileImage: p.profiles?.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
        bio: '',
        followerCount: 0,
        followingCount: 0,
        postCount: 0,
      })) || [],
      last_message_at: data.last_message_at,
      created_at: data.created_at,
      updated_at: data.updated_at,
      unread_count: 0,
      is_group: data.is_group,
      group_name: data.group_name,
      group_image: data.group_image,
    };
  } catch (error) {
    console.error('Error loading conversation:', error);
    return null;
  }
};

export const startConversation = async (userId1: string, userId2: string): Promise<string> => {
  try {
    // Check if conversation already exists
    const { data: existingConv, error: searchError } = await supabase
      .from('conversations')
      .select('id')
      .contains('participant_ids', [userId1])
      .contains('participant_ids', [userId2])
      .eq('is_group', false)
      .single();

    if (searchError && searchError.code !== 'PGRST116') throw searchError;

    if (existingConv) {
      return existingConv.id;
    }

    // Create new conversation
    const { data: newConv, error: createError } = await supabase
      .from('conversations')
      .insert([{
        participant_ids: [userId1, userId2],
        is_group: false,
        last_message_at: new Date().toISOString(),
      }])
      .select('id')
      .single();

    if (createError) throw createError;

    // Add participants
    const { error: participantsError } = await supabase
      .from('conversation_participants')
      .insert([
        { conversation_id: newConv.id, user_id: userId1 },
        { conversation_id: newConv.id, user_id: userId2 }
      ]);

    if (participantsError) throw participantsError;

    return newConv.id;
  } catch (error) {
    console.error('Error starting conversation:', error);
    throw error;
  }
};

// Message management
export const getMessages = async (conversationId: string, limit: number = 50): Promise<Message[]> => {
  try {
    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        sender:profiles!messages_sender_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        ),
        shared_post:posts (
          id,
          caption,
          post_media (
            media_url,
            media_type
          ),
          profiles:user_id (
            username
          )
        )
      `)
      .eq('conversation_id', conversationId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return (data || []).reverse().map(msg => ({
      id: msg.id,
      conversation_id: msg.conversation_id,
      sender_id: msg.sender_id,
      sender: {
        id: msg.sender?.id || msg.sender_id || '',
        username: msg.sender?.username || 'unknown',
        displayName: msg.sender?.full_name || msg.sender?.username || 'Unknown User',
        profileImage: msg.sender?.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
        bio: '',
        followerCount: 0,
        followingCount: 0,
        postCount: 0,
      },
      content: msg.content,
      message_type: msg.message_type,
      media_url: msg.media_url,
      shared_post_id: msg.shared_post_id,
      shared_post: msg.shared_post ? {
        id: msg.shared_post.id,
        caption: msg.shared_post.caption,
        media: msg.shared_post.post_media,
        user: {
          username: msg.shared_post.profiles?.username || 'unknown',
        }
      } : undefined,
      created_at: msg.created_at,
      updated_at: msg.updated_at,
      is_read: msg.is_read,
      is_deleted: msg.is_deleted,
    }));
  } catch (error) {
    console.error('Error loading messages:', error);
    return [];
  }
};

export const sendMessage = async (
  conversationId: string,
  senderId: string,
  content: string,
  messageType: 'text' | 'image' | 'post_share' = 'text',
  mediaUrl?: string,
  sharedPostId?: string
): Promise<void> => {
  try {
    console.log('📤 Sending message to database...');
    
    const { data: newMessage, error } = await supabase
      .from('messages')
      .insert([{
        conversation_id: conversationId,
        sender_id: senderId,
        content,
        message_type: messageType,
        media_url: mediaUrl,
        shared_post_id: sharedPostId,
      }])
      .select(`
        *,
        sender:profiles!messages_sender_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        )
      `)
      .single();

    if (error) throw error;

    console.log('✅ Message saved to database:', newMessage);

    // Update conversation last_message_at
    const { error: updateError } = await supabase
      .from('conversations')
      .update({ last_message_at: new Date().toISOString() })
      .eq('id', conversationId);

    if (updateError) throw updateError;

    // Transform and broadcast the message to other users
    const transformedMessage = {
      id: newMessage.id,
      conversation_id: newMessage.conversation_id,
      sender_id: newMessage.sender_id,
      sender: {
        id: newMessage.sender?.id || newMessage.sender_id || '',
        username: newMessage.sender?.username || 'unknown',
        displayName: newMessage.sender?.full_name || newMessage.sender?.username || 'Unknown User',
        profileImage: newMessage.sender?.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
        bio: '',
        followerCount: 0,
        followingCount: 0,
        postCount: 0,
      },
      content: newMessage.content,
      message_type: newMessage.message_type,
      media_url: newMessage.media_url,
      shared_post_id: newMessage.shared_post_id,
      created_at: newMessage.created_at,
      updated_at: newMessage.updated_at,
      is_read: newMessage.is_read,
      is_deleted: newMessage.is_deleted,
    };

    // Broadcast to other users in the conversation
    await realtimeService.broadcastNewMessage(conversationId, transformedMessage);
    
    console.log('📡 Message broadcasted to other users');
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

export const deleteMessage = async (messageId: string, userId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('messages')
      .update({ is_deleted: true })
      .eq('id', messageId)
      .eq('sender_id', userId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

export const markMessagesAsRead = async (conversationId: string, userId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('conversation_id', conversationId)
      .neq('sender_id', userId)
      .eq('is_read', false);

    if (error) throw error;
  } catch (error) {
    console.error('Error marking messages as read:', error);
    throw error;
  }
};

// Message requests for private accounts
export const sendMessageRequest = async (
  senderId: string,
  recipientId: string,
  message: string
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('message_requests')
      .insert([{
        sender_id: senderId,
        recipient_id: recipientId,
        message,
        status: 'pending'
      }]);

    if (error) throw error;
  } catch (error) {
    console.error('Error sending message request:', error);
    throw error;
  }
};

export const getMessageRequests = async (userId: string): Promise<MessageRequest[]> => {
  try {
    const { data, error } = await supabase
      .from('message_requests')
      .select(`
        *,
        sender:profiles!message_requests_sender_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        )
      `)
      .eq('recipient_id', userId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(req => ({
      id: req.id,
      sender_id: req.sender_id,
      sender: {
        id: req.sender?.id || req.sender_id || '',
        username: req.sender?.username || 'unknown',
        displayName: req.sender?.full_name || req.sender?.username || 'Unknown User',
        profileImage: req.sender?.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
        bio: '',
        followerCount: 0,
        followingCount: 0,
        postCount: 0,
      },
      recipient_id: req.recipient_id,
      message: req.message,
      status: req.status,
      created_at: req.created_at,
    }));
  } catch (error) {
    console.error('Error loading message requests:', error);
    return [];
  }
};

export const respondToMessageRequest = async (
  requestId: string,
  action: 'accept' | 'reject'
): Promise<string | null> => {
  try {
    if (action === 'accept') {
      // Get request details
      const { data: request, error: requestError } = await supabase
        .from('message_requests')
        .select('sender_id, recipient_id')
        .eq('id', requestId)
        .single();

      if (requestError) throw requestError;

      // Create conversation
      const conversationId = await startConversation(request.sender_id, request.recipient_id);

      // Update request status
      const { error: updateError } = await supabase
        .from('message_requests')
        .update({ status: 'accepted' })
        .eq('id', requestId);

      if (updateError) throw updateError;

      return conversationId;
    } else {
      // Just update status for rejection
      const { error } = await supabase
        .from('message_requests')
        .update({ status: 'rejected' })
        .eq('id', requestId);

      if (error) throw error;
      return null;
    }
  } catch (error) {
    console.error('Error responding to message request:', error);
    throw error;
  }
};